import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from "vue";
import { InputRules } from "@/rules";
import { ApprovalAttendanceResponse } from "@/models/approval/ApprovalResponse";
import { DateTime, Duration } from "luxon";
import { $alert } from "@/plugins/alert/alert";
import { $get, $post } from "@/plugins/api/api";
import { Paths } from "@/plugins/api/paths";
import {
  PulldownResponse,
  codeKbnFilter
} from "@/models/response/PulldownResponse";
import { CodeKbnList } from "@/models/codeMaster/Code";
import TimePicker2 from "@/components/TimePicker2.vue";

interface VForm extends Vue {
  reset(): void;
  resetValidation(): void;
  validate(): boolean;
}

export type WorkResults = {
  startTime?: string | null;
  endTime?: string | null;
  morningBreak?: string | null;
  lunchBreak?: string | null;
  midnightBreak?: string | null;
  opeTime?: string | null;
};
type WorkResultsKey = keyof WorkResults;

type ApprovalAttendanceResponseKey = keyof ApprovalAttendanceResponse;

type Headers<T> = {
  value: T;
  text: string;
  color?: string;
  rules?: Array<boolean | string | Function>;
};

type Props = {
  disabled: boolean;
  item: ApprovalAttendanceResponse;
};
type Emits = {
  (e: "close"): void;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'AttendanceDialog',
  props: {
    disabled: { type: Boolean },
    item: null
  },
  emits: ["close"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: "close"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  disabled: boolean;
  item: ApprovalAttendanceResponse;
};

const formatTime = (time: string | null | undefined) => {
  if (time) {
    const datetime = DateTime.fromISO(time);
    if (datetime.isValid) {
      const format = datetime.toFormat("HH:mm");
      if (format) {
        return format;
      }
    }
  }
  return undefined;
};

const initWorkResults = (value: WorkResults): WorkResults => {
  return {
    startTime: formatTime(value.startTime),
    endTime: formatTime(value.endTime),
    morningBreak: formatTime(value.morningBreak),
    lunchBreak: formatTime(value.lunchBreak),
    midnightBreak: formatTime(value.midnightBreak),
    opeTime: formatTime(value.opeTime)
  };
};

const endTime = ref<DateTime>();
const shiftBreakTime = ref<Duration>();
const overBreakTime = ref<Duration>();

const breakTimeRule = (val: unknown) => {
  const from = typeof val === "string" ? Duration.fromISOTime(val) : undefined;

  const shift = shiftBreakTime.value;
  const overwork = overBreakTime.value;

  if (!shift?.isValid || !from?.isValid) return;

  const to = overwork?.isValid ? shift.plus(overwork) : shift;

  const time = to.toISOTime({ suppressSeconds: true });
  const equalMessage = `${time}を入力してください。`;
  const greaterMessage = `${time}以上で入力してください。`;

  const equal = +from === +to || equalMessage;
  const lessThan = from >= to || greaterMessage;

  if (overwork?.isValid && overwork?.toMillis() !== 0) {
    return equal;
  } else {
    return lessThan;
  }
};
const endTimeRule = (val: unknown) => {
  const to = endTime.value;
  const from = typeof val === "string" ? DateTime.fromISO(val) : undefined;
  if (!from?.isValid || !to?.isValid) return true;
  const message = `${to?.toFormat("HH時mm分")}以上で入力して下さい。`;
  return from <= to || message;
};

const header: Headers<WorkResultsKey>[] = [
  { text: "開始時間： ", value: "startTime" },
  { text: "終了時間： ", value: "endTime" },
  { text: "休憩総時間： ", value: "lunchBreak" },
  { text: "早朝休憩時間： ", value: "morningBreak" },
  { text: "深夜休憩時間： ", value: "midnightBreak" },
  { text: "OPE時間", value: "opeTime" }
];

const aheader: Headers<ApprovalAttendanceResponseKey>[] = [
  { text: "開始時間： ", value: "afterStartTime" },
  { text: "終了時間： ", value: "afterEndTime", rules: [endTimeRule] },
  { text: "休憩総時間： ", value: "afterBreakTime" },
  { text: "早朝休憩時間： ", value: "afterMorningBreakTime" },
  { text: "深夜休憩時間： ", value: "afterMidnightBreakTime" },
  { text: "OPE時間", value: "afterOpeTime" }
];




const isOpen = ref(false);

const vform = ref<VForm>();

const isLoading = ref(false);

const dat = ref(Object.assign({}, props.item));

const reasonList = ref<string[]>([]);

const dispShift = computed(() =>
  initWorkResults({
    startTime: dat.value.shift.startTime,
    endTime: dat.value.shift.endTime,
    morningBreak: dat.value.shift.morningBreakTime,
    lunchBreak: dat.value.shift.breakTime,
    midnightBreak: dat.value.shift.midnightBreakTime,
    opeTime: dat.value.shift.opeTime
  })
);

const dispAttendance = computed(() =>
  initWorkResults({
    startTime: dat.value.preStartTime,
    endTime: dat.value.preEndTime,
    morningBreak: dat.value.preMorningBreakTime,
    lunchBreak: dat.value.preBreakTime,
    midnightBreak: dat.value.preMidnightBreakTime,
    opeTime: dat.value.preOpeTime
  })
);

const getCodeMaster = async () => {
  const code = await $get<PulldownResponse[]>(Paths.codeMasterPulldown);
  if (code) {
    reasonList.value = codeKbnFilter(code, CodeKbnList.ATTRSN).map(e => e.name);
  }
};

const onClose = () => {
  isOpen.value = false;
  emits("close");
};

const onSubmit = async () => {
  if (!vform.value?.validate()) {
    $alert("warning", "入力に誤りがあります。\n入力内容を確認してください。");
    return;
  }

  isLoading.value = true;
  const result = await $post(Paths.approval.attendance.update, dat.value);
  if (result) {
    $alert("success", "変更に成功しました。");
    onClose();
  } else {
    $alert("error", "変更に失敗しました。");
  }
  isLoading.value = false;
};

const getOverBreakTime = async (shiftId: string) => {
  return await $get<string>(
    `${Paths.approval.attendance.overBreakTime}/${shiftId}`
  );
};
const getShiftBreakTime = async (shiftId: string) => {
  return await $get<string>(
    `${Paths.approval.attendance.shiftBreakTime}/${shiftId}`
  );
};
const getEndTime = async (shiftId: string) => {
  return await $get<string>(`${Paths.approval.attendance.endTime}/${shiftId}`);
};

watch(
  () => props.item?.shiftId,
  async val => {
    if (val) {
      const obt = await getOverBreakTime(val);
      const sbt = await getShiftBreakTime(val);
      const et = await getEndTime(val);
      overBreakTime.value = obt ? Duration.fromISOTime(obt) : undefined;
      shiftBreakTime.value = sbt ? Duration.fromISOTime(sbt) : undefined;
      endTime.value = et ? DateTime.fromISO(et) : undefined;
    }
  },
  { immediate: true }
);

watch(isOpen, async val => {
  if (val) {
    await getCodeMaster();
    dat.value = Object.assign({}, props.item);
    dat.value.reasons.filter(e => reasonList.value.includes(e));
  }
});

return { __sfc: true,formatTime, initWorkResults, endTime, shiftBreakTime, overBreakTime, breakTimeRule, endTimeRule, header, aheader, props, emits, isOpen, vform, isLoading, dat, reasonList, dispShift, dispAttendance, getCodeMaster, onClose, onSubmit, getOverBreakTime, getShiftBreakTime, getEndTime, InputRules, TimePicker2 }
}

})